/**
 * Users API client for user search functionality.
 */

import apiClient from './client';
import type { UserSearchPage } from '../types/user';
import type { UserActivity } from '../types/user';

/**
 * Search for users by username.
 * Used primarily for finding users to message.
 * 
 * @param query - Search query string (username pattern)
 * @param page - Page number (0-indexed)
 * @param size - Page size
 * @returns Promise with paginated user search results
 */
export const searchUsers = async (
  query: string,
  page: number = 0,
  size: number = 20
): Promise<UserSearchPage> => {
  const response = await apiClient.get<UserSearchPage>('/users/search', {
    params: { q: query, page, size },
  });
  return response.data;
};

/**
 * Upload a profile picture for the current user.
 * 
 * @param file - Image file to upload
 * @returns Promise with the URL of the uploaded profile picture
 */
export const uploadProfilePicture = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await apiClient.post<{ url: string }>('/users/profile/picture', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data.url;
};

/**
 * Get the current user's profile picture URL.
 * 
 * @returns Promise with the URL of the user's profile picture, or null if not set
 */
export const getProfilePicture = async (): Promise<string | null> => {
  try {
    const response = await apiClient.get<{ url: string }>('/users/profile/picture');
    return response.data.url;
  } catch (error: any) {
    // If 404, user doesn't have a profile picture
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Delete the current user's profile picture.
 */
export const deleteProfilePicture = async (): Promise<void> => {
  await apiClient.delete('/users/profile/picture');
};

/**
 * Get profile picture URL for a user by their ID.
 * Constructs the URL based on the user ID and tries common image extensions.
 * 
 * @param userId - User ID (UUID string)
 * @returns URL to the profile picture, or null if not found
 */
export const getProfilePictureUrlByUserId = (userId: string): string | null => {
  if (!userId) return null;
  
  // Try common image extensions - the backend stores files as {userId}.{extension}
  // We'll let the browser try to load the image and handle 404s
  const extensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
  // Return the first extension - the component will handle fallback if image doesn't exist
  return `/api/users/profile/picture/${userId}${extensions[0]}`;
};

/**
 * Get activity statistics for the current user.
 * 
 * @returns Promise with user activity statistics
 */
export const getUserActivity = async (): Promise<UserActivity> => {
  const response = await apiClient.get<UserActivity>('/users/profile/activity');
  return response.data;
};

export const changePassword = async (newPassword: string): Promise<void> => {
  try {
    await apiClient.put('/users/profile/password', { newPassword });
  } catch (err: any) {
    const serverMsg = err?.response?.data?.error;
    throw new Error(serverMsg || 'Failed to change password');
  }
};
